// /admin/usuarios/:id — Ficha 360 del cliente.
// Pensada como base para segmentación de campañas: tags + notas + métricas.
// Mobile-first. Sin librerías de gráficos (SVG inline).
//
// Decisión 2026-06-15: página dedicada (no modal) para tener espacio de gráficos
// y poder linkear cada cliente directo (ej: desde campañas, WA, etc.)

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { formatearCI, validarCI } from '../../lib/documentos'

// ─────────────── helpers ───────────────
const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'
const fmtFechaCorta = (d) => d ? new Date(d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' }) : '—'
const fmtMoney = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const diasDesde = (d) => d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null

const ROLE_LABEL = {
  superadmin: 'Superadmin', admin: 'Admin', marketing: 'Marketing',
  repartidor: 'Repartidor', portero: 'Portero', cliente: 'Cliente',
}
const ROLE_COLOR = {
  superadmin: { bg: '#fef3c7', color: '#92400e' },
  admin:      { bg: '#dbeafe', color: '#1e40af' },
  marketing:  { bg: '#fce7f3', color: '#9d174d' },
  repartidor: { bg: '#d1fae5', color: '#065f46' },
  portero:    { bg: '#e0e7ff', color: '#3730a3' },
  cliente:    { bg: '#f1f5f9', color: '#475569' },
}

// Tags sugeridos pre-armados (Nico puede agregar libres)
const TAGS_SUGERIDOS = [
  'vip', 'recomendador', 'riesgo-churn', 'vecino', 'portero', 'beta',
  'b2b-mini', 'fuera-zona', 'pago-transferencia', 'no-molestar',
]

const TAG_COLORS = {
  'vip':              { bg: '#fef3c7', color: '#92400e', icon: '⭐' },
  'recomendador':     { bg: '#dcfce7', color: '#166534', icon: '🤝' },
  'riesgo-churn':     { bg: '#fee2e2', color: '#991b1b', icon: '⚠️' },
  'vecino':           { bg: '#e0f2fe', color: '#075985', icon: '🏠' },
  'portero':          { bg: '#e0e7ff', color: '#3730a3', icon: '🛎️' },
  'beta':             { bg: '#f3e8ff', color: '#6b21a8', icon: '🧪' },
  'b2b-mini':         { bg: '#fef3c7', color: '#854d0e', icon: '🏢' },
  'fuera-zona':       { bg: '#fed7aa', color: '#9a3412', icon: '🚫' },
  'pago-transferencia': { bg: '#cffafe', color: '#0e7490', icon: '🏦' },
  'no-molestar':      { bg: '#fecaca', color: '#7f1d1d', icon: '🤐' },
}

// ─────────────── página principal ───────────────
export default function UsuarioDetalle() {
  const { id } = useParams()
  const nav = useNavigate()
  const { isSuperadmin } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  // Datos cargados
  const [perfil, setPerfil] = useState(null)
  const [barrios, setBarrios] = useState([])
  const [suscripciones, setSuscripciones] = useState([])
  const [pedidos, setPedidos] = useState([])
  const [facturas, setFacturas] = useState([])
  const [perfilesFact, setPerfilesFact] = useState([])
  const [waMessages, setWaMessages] = useState([])
  const [emailsLog, setEmailsLog] = useState([])
  const [carrito, setCarrito] = useState(null)   // carrito guardado en la cuenta (abandonado / con error)

  // Por rol específico
  const [repartidorInfo, setRepartidorInfo] = useState(null)   // { vehiculo, zona, activo, entregas_30d, entregas_total, exito_pct }
  const [porteroInfo, setPorteroInfo] = useState(null)         // { edificio, unidades, referidos_activos, slug, lugar }

  // Form editable (datos personales + permisos + tags + notas)
  const [form, setForm] = useState({
    nombre: '', email: '', phone: '',
    direccion: '', apto: '', barrio_id: '',
    documento_tipo: 'ci', documento: '',
    transferencia_habilitada: false,
    tags: [],
    notas_admin: '',
  })

  // UI
  const [tab, setTab] = useState('datos')

  // Renovar clave: modal con magic link para copiar/mandar por WhatsApp
  const [renovClave, setRenovClave] = useState(null) // { loading, link, error }
  const [tagInput, setTagInput] = useState('')
  const [savedFlash, setSavedFlash] = useState(false)

  // ─────────── carga inicial ───────────
  useEffect(() => {
    let alive = true
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [
          { data: p, error: ep },
          { data: bs },
          { data: pf },
          { data: subs },
          { data: peds },
          { data: facts },
          { data: emails },
          { data: carritoRow },
        ] = await Promise.all([
          supabase.from('profiles')
            .select('id, nombre, email, phone, direccion, apto, barrio_id, documento_tipo, documento, role, created_at, transferencia_habilitada, transferencia_habilitada_at, tags, notas_admin')
            .eq('id', id).single(),
          supabase.from('barrios').select('id, nombre').eq('activo', true).order('nombre'),
          supabase.from('perfiles_facturacion')
            .select('id, alias, tipo, razon_social, rut, email_fiscal, es_default')
            .eq('user_id', id),
          supabase.from('clientes')
            .select(`
              id, alias, estado, kit_actual, fecha_inicio, fecha_proximo_cobro, pausa_hasta,
              dia_entrega, ventana_horaria, semana_mes, tipo_vivienda, fuera_zona, entrega_responsable,
              direccion, apto, lat, lng, geocoded_at, notas,
              modelo_cobro, dia_cobro_mes, mp_card_last4, mp_card_brand, mp_preapproval_status,
              cancelado_at:cancelled_at, cancelacion_motivo, paused_at, reactivated_at,
              titular_id, beneficiario_id, portero_referido_id, empresa_id,
              barrio_id, barrio_obj:barrios(nombre),
              kit:kits(nombre, codigo),
              portero:porteros(lugar, edificio_nombre, direccion),
              empresa:empresas(id, razon_social, rut, tipo, email_facturacion, telefono, contacto_principal_nombre, contacto_principal_cargo, estado_pipeline, facturacion_modo)
            `)
            .or(`titular_id.eq.${id},beneficiario_id.eq.${id}`)
            .order('fecha_inicio', { ascending: false }),
          supabase.from('pedidos')
            .select('id, numero_orden, estado, metodo_pago, monto_total, created_at, fecha_entrega_programada')
            .eq('comprador_profile_id', id)
            .order('created_at', { ascending: false }),
          supabase.from('facturas_emitidas')
            .select('id, serie, numero, tipo_cfe, fecha_emision, monto_total, shortlink_hash')
            .eq('cliente_profile_id', id)
            .order('fecha_emision', { ascending: false }),
          // Emails enviados
          supabase.from('emails_log').select('id, asunto, template, sent_at, status').eq('user_id', id).order('sent_at', { ascending: false }).limit(20),
          // Carrito guardado en la cuenta (abandonado / con error) — feature 26-jul-2026
          supabase.from('carritos_cliente')
            .select('items, ultimo_paso, ultimo_error, error_at, updated_at')
            .eq('user_id', id).maybeSingle(),
        ])
        if (!alive) return
        if (ep) throw ep
        if (!p) throw new Error('Usuario no encontrado')
        setPerfil(p)
        setBarrios(bs || [])
        setPerfilesFact(pf || [])
        setSuscripciones(subs || [])
        setPedidos(peds || [])
        setFacturas(facts || [])
        setEmailsLog(emails || [])
        setCarrito(carritoRow || null)

        // WA: busco por teléfono del usuario
        if (p.phone) {
          const cleanPhone = p.phone.replace(/\D/g, '')
          const variants = [cleanPhone, cleanPhone.replace(/^598/, ''), cleanPhone.replace(/^0/, '598')]
          // Fix 7-jul-2026 (auditoría): columnas reales son last_message_at,
          // last_message_preview, phone. El bloque WA de la ficha 360 nunca cargaba.
          const { data: convs } = await supabase
            .from('conversations')
            .select('id, last_message_at, last_message_preview, unread_count, phone')
            .in('phone', variants)
            .order('last_message_at', { ascending: false })
            .limit(5)
          if (alive) setWaMessages(convs || [])
        }

        // ─── Carga adicional según rol ───
        if (p.role === 'repartidor') {
          const hace30 = new Date(Date.now() - 30 * 86400000).toISOString()
          const [{ data: rep }, { data: e30 }, { data: eTotal }] = await Promise.all([
            supabase.from('repartidores').select('vehiculo, zona, activo, notas').eq('id', p.id).maybeSingle(),
            supabase.from('entregas').select('estado').eq('repartidor_id', p.id).gte('created_at', hace30),
            supabase.from('entregas').select('id', { count: 'exact', head: true }).eq('repartidor_id', p.id),
          ])
          if (alive) {
            const e30Arr = e30 || []
            const exitosas30 = e30Arr.filter(e => e.estado === 'entregada').length
            setRepartidorInfo({
              vehiculo: rep?.vehiculo,
              zona: rep?.zona,
              activo: rep?.activo,
              notas: rep?.notas,
              entregas_30d: e30Arr.length,
              entregas_exitosas_30d: exitosas30,
              exito_pct_30d: e30Arr.length > 0 ? Math.round((exitosas30 / e30Arr.length) * 100) : null,
              entregas_total: eTotal?.length ?? 0,
            })
          }
        }
        if (p.role === 'portero') {
          const [{ data: po }, { data: refs }] = await Promise.all([
            supabase.from('porteros').select('edificio_nombre, lugar, direccion, cant_unidades, slug, activo, banco_cuenta, turno').eq('id', p.id).maybeSingle(),
            supabase.from('clientes').select('id, estado').eq('portero_referido_id', p.id),
          ])
          if (alive) {
            const refsArr = refs || []
            setPorteroInfo({
              ...po,
              referidos_activos: refsArr.filter(r => r.estado === 'activa').length,
              referidos_total: refsArr.length,
            })
          }
        }

        setForm({
          nombre: p.nombre || '',
          email: p.email || '',
          phone: p.phone || '',
          direccion: p.direccion || '',
          apto: p.apto || '',
          barrio_id: p.barrio_id || '',
          documento_tipo: p.documento_tipo || 'ci',
          documento: p.documento || '',
          transferencia_habilitada: !!p.transferencia_habilitada,
          tags: Array.isArray(p.tags) ? p.tags : [],
          notas_admin: p.notas_admin || '',
        })
      } catch (e) {
        if (alive) setError(e.message || String(e))
      } finally {
        if (alive) setLoading(false)
      }
    }
    load()
    return () => { alive = false }
  }, [id])

  // ─────────── KPIs derivados ───────────
  const kpis = useMemo(() => {
    const pagadosOk = pedidos.filter(p => p.estado === 'pagado' || p.estado === 'entregado')
    const ltv = pagadosOk.reduce((s, p) => s + Number(p.monto_total || 0), 0)
    const ticket = pagadosOk.length > 0 ? ltv / pagadosOk.length : 0
    const pendiente = pedidos.filter(p => p.estado === 'pendiente_pago').reduce((s, p) => s + Number(p.monto_total || 0), 0)
    const ultimaCompra = pedidos[0]?.fecha_entrega_programada || pedidos[0]?.created_at
    const dias = diasDesde(ultimaCompra)
    const primerPedido = pedidos[pedidos.length - 1]?.created_at
    const mesesActivo = primerPedido
      ? Math.max(1, (Date.now() - new Date(primerPedido).getTime()) / (86400000 * 30))
      : 0
    const frecuencia = mesesActivo > 0 ? pedidos.length / mesesActivo : 0

    let churnRisk = 'sin-data'
    let churnLabel = 'Sin compras'
    let churnColor = '#94a3b8'
    if (dias !== null) {
      if (dias < 30) { churnRisk = 'verde'; churnLabel = 'Activo'; churnColor = '#16a34a' }
      else if (dias < 60) { churnRisk = 'amber'; churnLabel = 'En riesgo'; churnColor = '#f59e0b' }
      else { churnRisk = 'rojo'; churnLabel = 'Inactivo'; churnColor = '#dc2626' }
    }

    return { ltv, ticket, pendiente, dias, frecuencia, churnRisk, churnLabel, churnColor, totalPedidos: pedidos.length }
  }, [pedidos])

  // Gasto mensual últimos 12 meses (para mini bar chart)
  const gastoMensual = useMemo(() => {
    const ahora = new Date()
    const meses = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(ahora.getFullYear(), ahora.getMonth() - i, 1)
      meses.push({
        label: d.toLocaleDateString('es-UY', { month: 'short' }).replace('.', ''),
        year: d.getFullYear(),
        month: d.getMonth(),
        total: 0,
      })
    }
    pedidos.forEach(p => {
      if (p.estado !== 'pagado' && p.estado !== 'entregado') return
      const d = new Date(p.created_at)
      const idx = meses.findIndex(m => m.year === d.getFullYear() && m.month === d.getMonth())
      if (idx >= 0) meses[idx].total += Number(p.monto_total || 0)
    })
    const max = Math.max(1, ...meses.map(m => m.total))
    return { meses, max }
  }, [pedidos])

  // Timeline mezclado (pedidos + WA + emails)
  const timeline = useMemo(() => {
    const items = []
    pedidos.slice(0, 12).forEach(p => items.push({
      tipo: 'pedido', at: p.created_at,
      titulo: `Pedido ${p.numero_orden}`,
      subtitulo: `${p.estado} · ${p.metodo_pago || '—'} · ${fmtMoney(p.monto_total)}`,
      icon: '🛒',
    }))
    waMessages.slice(0, 8).forEach(w => items.push({
      tipo: 'wa', at: w.last_message_at,
      titulo: 'Mensaje WhatsApp',
      subtitulo: (w.last_message_preview || '').slice(0, 80),
      icon: '💬',
    }))
    emailsLog.slice(0, 10).forEach(e => items.push({
      tipo: 'email', at: e.sent_at,
      titulo: e.asunto || e.template,
      subtitulo: `Email · ${e.status || 'enviado'}`,
      icon: '📧',
    }))
    facturas.slice(0, 8).forEach(f => {
      const esNC = f.tipo_cfe === 'e_nota_credito'
      items.push({
        tipo: 'factura', at: f.fecha_emision,
        titulo: `${esNC ? 'NC' : 'Factura'} ${f.serie}-${f.numero}`,
        subtitulo: `${esNC ? '−' : ''}${fmtMoney(f.monto_total)}`,
        icon: esNC ? '↩' : '🧾',
      })
    })
    items.sort((a, b) => new Date(b.at) - new Date(a.at))
    return items.slice(0, 25)
  }, [pedidos, waMessages, emailsLog, facturas])

  // ─────────── handlers ───────────
  async function guardar() {
    if (!isSuperadmin && perfil?.role === 'superadmin') {
      setError('Solo superadmin puede editar otro superadmin')
      return
    }
    setSaving(true)
    setError('')
    try {
      const patch = {
        nombre: form.nombre || null,
        email: form.email || null,
        phone: form.phone || null,
        direccion: form.direccion || null,
        apto: form.apto || null,
        barrio_id: form.barrio_id || null,
        documento_tipo: form.documento_tipo || null,
        documento: form.documento || null,
        transferencia_habilitada: !!form.transferencia_habilitada,
        tags: form.tags,
        notas_admin: form.notas_admin || null,
      }
      // Solo superadmin actualiza el flag de transferencia
      if (!isSuperadmin) delete patch.transferencia_habilitada

      const { error: ue } = await supabase.from('profiles').update(patch).eq('id', id)
      if (ue) throw ue

      setPerfil(p => ({ ...p, ...patch }))
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1800)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  // ¿Hay ediciones sin guardar? El botón "Guardar cambios" vive en la barra superior; al editar
  // la Dirección (más abajo) quedaba fuera de pantalla y se perdían los datos sin aviso
  // (caso real 26-jul: la dirección de una clienta se tipeó y nunca se guardó).
  const hayCambios = useMemo(() => {
    if (!perfil) return false
    const norm = (v) => (v === null || v === undefined || v === '' ? null : v)
    const campos = ['nombre', 'email', 'phone', 'direccion', 'apto', 'barrio_id', 'documento_tipo', 'documento', 'notas_admin']
    if (campos.some((k) => norm(form[k]) !== norm(perfil[k]))) return true
    if (!!form.transferencia_habilitada !== !!perfil.transferencia_habilitada) return true
    return JSON.stringify(form.tags || []) !== JSON.stringify(perfil.tags || [])
  }, [form, perfil])

  // Avisar si intenta cerrar/recargar con cambios pendientes.
  useEffect(() => {
    if (!hayCambios) return
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [hayCambios])

  function addTag(t) {
    const clean = (t || '').trim().toLowerCase().replace(/\s+/g, '-').slice(0, 30)
    if (!clean) return
    if (form.tags.includes(clean)) return
    setForm(f => ({ ...f, tags: [...f.tags, clean] }))
    setTagInput('')
  }
  function removeTag(t) {
    setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }))
  }

  // WA + email rápidos
  const telE164 = perfil?.phone ? (() => {
    const t = perfil.phone.replace(/\D/g, '')
    return t.startsWith('598') ? t : t.startsWith('0') ? '598' + t.slice(1) : '598' + t
  })() : null

  // Renovar clave: llama a edge fn admin-renovar-clave
  async function renovarClave() {
    if (!perfil?.email) { alert('El usuario no tiene email'); return }
    if (!confirm(`¿Generar link de renovación de clave para ${perfil.nombre || perfil.email}?`)) return
    setRenovClave({ loading: true })
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-renovar-clave`
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ profile_id: perfil.id }),
      })
      const j = await res.json()
      if (!res.ok || !j.action_link) throw new Error(j.error || 'No se pudo generar el link')
      setRenovClave({ loading: false, link: j.action_link })
    } catch (e) {
      setRenovClave({ loading: false, error: e.message || String(e) })
    }
  }

  // ─────────── render ───────────
  if (loading) {
    return (
      <AdminLayout title="Cargando…">
        <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Cargando ficha…</div>
      </AdminLayout>
    )
  }
  if (!perfil) {
    return (
      <AdminLayout title="Usuario no encontrado">
        <div style={{ padding: 24 }}>
          <p style={{ color: '#94a3b8' }}>{error || 'No se encontró el usuario'}</p>
          <button onClick={() => nav('/admin/usuarios')} style={btnSecond}>← Volver a Usuarios</button>
        </div>
      </AdminLayout>
    )
  }

  const tieneSuscriptor = suscripciones.length > 0
  const role = perfil.role
  const roleStyle = ROLE_COLOR[role] || ROLE_COLOR.cliente

  // Clasificación cliente: rol cliente o cualquiera con actividad de cliente (admins test, etc.)
  const esCliente = role === 'cliente' || suscripciones.length > 0 || pedidos.length > 0
  const esInterno = ['admin', 'superadmin', 'marketing'].includes(role)
  const esRepartidor = role === 'repartidor'
  const esPortero = role === 'portero'
  const esB2B = suscripciones.some(s => s.empresa_id)
  const empresaB2B = suscripciones.find(s => s.empresa)?.empresa

  return (
    <AdminLayout title={`Ficha · ${perfil.nombre || perfil.email}`}>
      <div style={wrap}>
        {/* Top bar: back + acciones rápidas */}
        <div className="ud-topbar" style={topBar}>
          <button onClick={() => nav('/admin/usuarios')} style={btnBack}>← Usuarios</button>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {telE164 && (
              <Link to={`/admin/crm?phone=${telE164}`} style={btnQuickCRM} title="Abrir conversación en CRM interno">
                💬 CRM
              </Link>
            )}
            {telE164 && (
              <a href={`https://wa.me/${telE164}`} target="_blank" rel="noopener noreferrer" style={btnQuickWa} title="Abrir WhatsApp externo (sin guardar en CRM)">
                ↗ WA
              </a>
            )}
            {perfil.email && (
              <a href={`mailto:${perfil.email}`} style={btnQuickMail}>📧 Email</a>
            )}
            {perfil.email && (
              <button onClick={renovarClave} style={btnQuickClave} title="Generar link para que renueve su contraseña">
                🔑 Renovar clave
              </button>
            )}
            <button onClick={guardar} disabled={saving} style={btnGuardar}>
              {saving ? 'Guardando…' : savedFlash ? '✓ Guardado' : '💾 Guardar cambios'}
            </button>
          </div>
        </div>

        {/* HERO: avatar + nombre + badges */}
        <div className="ud-hero" style={hero}>
          <div style={avatar(roleStyle.bg)}>
            <span style={{ color: roleStyle.color, fontSize: 28, fontWeight: 900 }}>
              {(perfil.nombre || perfil.email || '?').charAt(0).toUpperCase()}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={heroNombre}>{perfil.nombre || 'Sin nombre'}</h1>
            <div style={heroSub}>
              {perfil.email && <span>📧 {perfil.email}</span>}
              {perfil.phone && <span>📞 {perfil.phone}</span>}
            </div>
            <div style={badgesRow}>
              <span style={pill(roleStyle.bg, roleStyle.color)}>🛡 {ROLE_LABEL[role] || role}</span>
              {esB2B && <span style={pill('#fef3c7', '#854d0e')}>🏢 B2B</span>}
              {!esB2B && tieneSuscriptor && role === 'cliente' && <span style={pill('#e0f2fe', '#075985')}>👤 B2C</span>}
              {tieneSuscriptor && <span style={pill('#dcfce7', '#166534')}>✓ Suscriptor activo</span>}
              {perfil.transferencia_habilitada && <span style={pill('#cffafe', '#0e7490')}>🏦 Transferencia OK</span>}
              <span style={pill(`${kpis.churnColor}22`, kpis.churnColor)}>● {kpis.churnLabel}</span>
              {form.tags.slice(0, 5).map(t => {
                const tc = TAG_COLORS[t] || { bg: '#f1f5f9', color: '#475569', icon: '🏷' }
                return <span key={t} style={pill(tc.bg, tc.color)}>{tc.icon} {t}</span>
              })}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
              Alta: {fmtFecha(perfil.created_at)}
            </div>
          </div>
        </div>

        {error && <div style={errBox}>{error}</div>}

        {/* KPIs strip — para clientes (B2C/B2B) */}
        {(role === 'cliente' || tieneSuscriptor || pedidos.length > 0) && (
          <div style={kpiGrid}>
            <Kpi label="LTV" value={fmtMoney(kpis.ltv)} color="#16a34a" hint="Pagado total" />
            <Kpi label="Ticket prom." value={fmtMoney(kpis.ticket)} hint="Por pedido" />
            <Kpi label="Pedidos" value={kpis.totalPedidos} hint="Total histórico" />
            <Kpi label="Frecuencia" value={kpis.frecuencia.toFixed(1)} hint="ped/mes" />
            <Kpi label="Última compra" value={kpis.dias != null ? `${kpis.dias}d` : '—'} hint="Días atrás" color={kpis.churnColor} />
            {kpis.pendiente > 0 && <Kpi label="Pendiente" value={fmtMoney(kpis.pendiente)} color="#f59e0b" hint="Sin pagar" />}
          </div>
        )}

        {/* KPIs Repartidor */}
        {esRepartidor && repartidorInfo && (
          <>
            <div style={kpiGrid}>
              <Kpi label="Entregas 30d" value={repartidorInfo.entregas_30d} hint={`${repartidorInfo.entregas_exitosas_30d} ok`} color="#1a3461" />
              <Kpi label="% éxito 30d" value={repartidorInfo.exito_pct_30d != null ? `${repartidorInfo.exito_pct_30d}%` : '—'}
                color={repartidorInfo.exito_pct_30d >= 90 ? '#16a34a' : repartidorInfo.exito_pct_30d >= 75 ? '#f59e0b' : '#dc2626'} />
              <Kpi label="Entregas total" value={repartidorInfo.entregas_total} hint="Vida" />
              <Kpi label="Estado" value={repartidorInfo.activo ? 'Activo' : 'Inactivo'} color={repartidorInfo.activo ? '#16a34a' : '#94a3b8'} />
            </div>
            <div style={card}>
              <div style={cardTitle}>🚐 Repartidor</div>
              <Grid2>
                <FieldL label="Vehículo"><div style={ro}>{repartidorInfo.vehiculo || '—'}</div></FieldL>
                <FieldL label="Zona asignada"><div style={ro}>{repartidorInfo.zona || '—'}</div></FieldL>
              </Grid2>
              {repartidorInfo.notas && (
                <FieldL label="Notas"><div style={{ ...ro, whiteSpace: 'pre-wrap' }}>{repartidorInfo.notas}</div></FieldL>
              )}
              <Link to={`/admin/repartidores/${perfil.id}`} style={{ ...btnLink, marginTop: 6, display: 'inline-block' }}>📈 Ver dashboard completo de performance →</Link>
            </div>
          </>
        )}

        {/* KPIs Portero */}
        {esPortero && porteroInfo && (
          <>
            <div style={kpiGrid}>
              <Kpi label="Vecinos activos" value={porteroInfo.referidos_activos || 0} hint="Referidos" color="#16a34a" />
              <Kpi label="Referidos total" value={porteroInfo.referidos_total || 0} hint="Vida" />
              <Kpi label="Unidades edificio" value={porteroInfo.cant_unidades || '—'} hint="Total" />
              <Kpi label="Penetración" value={porteroInfo.cant_unidades > 0 ? `${Math.round((porteroInfo.referidos_activos / porteroInfo.cant_unidades) * 100)}%` : '—'} hint="Vecinos/unidades" />
              <Kpi label="Estado" value={porteroInfo.activo ? 'Activo' : 'Inactivo'} color={porteroInfo.activo ? '#16a34a' : '#94a3b8'} />
            </div>
            <div style={card}>
              <div style={cardTitle}>🛎️ Edificio referido</div>
              <Grid2>
                <FieldL label="Edificio / lugar"><div style={ro}>{porteroInfo.edificio_nombre || porteroInfo.lugar || '—'}</div></FieldL>
                <FieldL label="Turno"><div style={ro}>{porteroInfo.turno || '—'}</div></FieldL>
                <FieldL label="Dirección"><div style={ro}>{porteroInfo.direccion || '—'}</div></FieldL>
                <FieldL label="Slug / código"><div style={ro}>{porteroInfo.slug || '—'}</div></FieldL>
              </Grid2>
              {porteroInfo.banco_cuenta && (
                <FieldL label="Cuenta bancaria (comisiones)"><div style={ro}>{porteroInfo.banco_cuenta}</div></FieldL>
              )}
            </div>
          </>
        )}

        {/* Bloque Empresa B2B — datos completos de la empresa que factura */}
        {esB2B && empresaB2B && (
          <div style={card}>
            <div style={cardTitle}>🏢 Cliente B2B · {empresaB2B.razon_social}</div>
            <Grid2>
              <FieldL label="Razón social"><div style={ro}>{empresaB2B.razon_social || '—'}</div></FieldL>
              <FieldL label="RUT"><div style={{ ...ro, fontFamily: 'monospace', fontWeight: 700 }}>{empresaB2B.rut || '—'}</div></FieldL>
              <FieldL label="Tipo"><div style={ro}>{empresaB2B.tipo ? capitalize(empresaB2B.tipo) : '—'}</div></FieldL>
              <FieldL label="Modo facturación"><div style={ro}>{empresaB2B.facturacion_modo ? capitalize(empresaB2B.facturacion_modo.replace(/_/g, ' ')) : '—'}</div></FieldL>
              <FieldL label="Email facturación"><div style={ro}>{empresaB2B.email_facturacion || '—'}</div></FieldL>
              <FieldL label="Teléfono empresa"><div style={ro}>{empresaB2B.telefono || '—'}</div></FieldL>
              <FieldL label="Contacto principal"><div style={ro}>{empresaB2B.contacto_principal_nombre || '—'}{empresaB2B.contacto_principal_cargo ? ` · ${empresaB2B.contacto_principal_cargo}` : ''}</div></FieldL>
              <FieldL label="Estado pipeline"><div style={ro}>{empresaB2B.estado_pipeline ? capitalize(empresaB2B.estado_pipeline) : '—'}</div></FieldL>
            </Grid2>
            {empresaB2B.id && (
              <Link to={`/admin/empresas/${empresaB2B.id}`} style={{ ...btnLink, marginTop: 8, display: 'inline-block' }}>🏢 Abrir ficha completa de la empresa →</Link>
            )}
          </div>
        )}

        {/* Gráfico gasto mensual — solo si hay actividad de cliente */}
        {(tieneSuscriptor || pedidos.length > 0) && (
          <div style={card}>
            <div style={cardTitle}>📊 Gasto últimos 12 meses</div>
            <MiniBarChart data={gastoMensual.meses} max={gastoMensual.max} />
          </div>
        )}

        {/* Tags + Notas internas */}
        <div className="ud-twocol" style={twoCol}>
          <div style={card}>
            <div style={cardTitle}>🏷 Tags <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>· segmentación campañas</span></div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {form.tags.length === 0 && <span style={{ color: '#cbd5e1', fontSize: 12, fontStyle: 'italic' }}>Sin tags todavía</span>}
              {form.tags.map(t => {
                const tc = TAG_COLORS[t] || { bg: '#f1f5f9', color: '#475569', icon: '🏷' }
                return (
                  <span key={t} style={{ ...pill(tc.bg, tc.color), display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {tc.icon} {t}
                    <button onClick={() => removeTag(t)} style={tagX}>×</button>
                  </span>
                )
              })}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text" placeholder="agregar tag…"
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(tagInput) } }}
                style={inp}
              />
              <button onClick={() => addTag(tagInput)} style={btnSecond}>+ Agregar</button>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {TAGS_SUGERIDOS.filter(t => !form.tags.includes(t)).slice(0, 8).map(t => (
                <button key={t} onClick={() => addTag(t)} style={tagSug}>+ {t}</button>
              ))}
            </div>
          </div>

          <div style={card}>
            <div style={cardTitle}>📝 Notas internas <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>· solo equipo</span></div>
            <textarea
              value={form.notas_admin}
              onChange={e => setForm(f => ({ ...f, notas_admin: e.target.value }))}
              placeholder="Ej: Cliente exigente, prefiere entregas matinales. Recomendó a 3 vecinos en mayo."
              rows={4}
              style={textarea}
            />
          </div>
        </div>

        {/* Tabs — filtradas según rol */}
        <div className="ud-tabs" style={tabsBar}>
          {[
            { id: 'datos', l: '📋 Datos', show: true },
            { id: 'direccion', l: '📍 Dirección', show: true },
            { id: 'permisos', l: '🔑 Permisos', show: true },
            { id: 'suscripciones', l: `📦 Suscripciones${suscripciones.length ? ` (${suscripciones.length})` : ''}`, show: suscripciones.length > 0 || role === 'cliente' },
            { id: 'pedidos',       l: `🛒 Pedidos${pedidos.length ? ` (${pedidos.length})` : ''}`,                   show: pedidos.length > 0 || role === 'cliente' },
            { id: 'facturas',      l: `🧾 Facturas${facturas.length ? ` (${facturas.length})` : ''}`,                 show: facturas.length > 0 },
            { id: 'facturacion',   l: `💼 Facturación${perfilesFact.length ? ` (${perfilesFact.length})` : ''}`,      show: perfilesFact.length > 0 || role === 'cliente' },
          ].filter(t => t.show).map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={tab === t.id ? tabActive : tabBtn}>{t.l}</button>
          ))}
        </div>

        <div style={card}>
          {tab === 'datos' && (
            <Grid2>
              <FieldL label="Nombre">
                <input style={inp} value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} />
              </FieldL>
              <FieldL label="Email">
                <input style={inp} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </FieldL>
              <FieldL label="Teléfono">
                <input style={inp} type="tel" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              </FieldL>
              <FieldL label="Documento" hint={form.documento_tipo === 'ci' ? '7 u 8 dígitos, con o sin puntos y guión' : undefined}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select style={{ ...inp, maxWidth: 110 }} value={form.documento_tipo} onChange={e => setForm(f => ({ ...f, documento_tipo: e.target.value }))}>
                    <option value="ci">CI</option>
                    <option value="rut">RUT</option>
                    <option value="pasaporte">Pasaporte</option>
                  </select>
                  <input
                    style={inp}
                    value={form.documento}
                    onChange={e => setForm(f => ({ ...f, documento: e.target.value }))}
                    onBlur={e => {
                      const v = e.target.value
                      if (v && form.documento_tipo === 'ci' && validarCI(v)) {
                        setForm(f => ({ ...f, documento: formatearCI(v) }))
                      }
                    }}
                    placeholder={form.documento_tipo === 'ci' ? 'Ej: 36955508' : form.documento_tipo === 'rut' ? '212345670012' : 'AB123456'}
                    inputMode={form.documento_tipo === 'pasaporte' ? 'text' : 'numeric'}
                  />
                </div>
              </FieldL>
            </Grid2>
          )}

          {tab === 'direccion' && (
            <>
              <FieldL label="Calle y número">
                <input style={inp} value={form.direccion} onChange={e => setForm(f => ({ ...f, direccion: e.target.value }))} placeholder="18 de Julio 1234" />
              </FieldL>
              <Grid2>
                <FieldL label="Apto">
                  <input style={inp} value={form.apto} onChange={e => setForm(f => ({ ...f, apto: e.target.value }))} placeholder="201" />
                </FieldL>
                <FieldL label="Barrio">
                  <select style={inp} value={form.barrio_id || ''} onChange={e => setForm(f => ({ ...f, barrio_id: e.target.value }))}>
                    <option value="">— elegir —</option>
                    {barrios.map(b => <option key={b.id} value={b.id}>{b.nombre}</option>)}
                  </select>
                </FieldL>
              </Grid2>
            </>
          )}

          {tab === 'permisos' && (
            <>
              {/* Transferencia bancaria — solo para clientes (B2C/B2B). No aplica a internos, repartidor, portero */}
              {role === 'cliente' ? (
                <div style={{ ...permisosBox }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: isSuperadmin ? 'pointer' : 'default' }}>
                    <input
                      type="checkbox" checked={form.transferencia_habilitada}
                      onChange={e => setForm(f => ({ ...f, transferencia_habilitada: e.target.checked }))}
                      disabled={!isSuperadmin}
                      style={{ width: 22, height: 22, marginTop: 2, cursor: 'inherit' }}
                    />
                    <div>
                      <div style={{ fontWeight: 800, color: '#1a3461' }}>🏦 Transferencia bancaria habilitada</div>
                      <div style={{ fontSize: 12, color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
                        El cliente verá "Pagar por transferencia" en el checkout.<br/>
                        Roberto confirma manual desde <strong>/admin/cobros-pendientes</strong>.<br/>
                        Cuando habilités, le mandamos un email automático con los datos bancarios.
                      </div>
                      {perfil.transferencia_habilitada_at && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                          Habilitada el {fmtFecha(perfil.transferencia_habilitada_at)}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              ) : (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10 }}>
                  Este usuario tiene rol <strong style={{ color: '#475569' }}>{ROLE_LABEL[role] || role}</strong> — sin permisos especiales configurables.
                  <div style={{ marginTop: 6, fontSize: 11 }}>
                    Los permisos especiales (transferencia bancaria, etc.) aplican solo a clientes.
                  </div>
                </div>
              )}
            </>
          )}

          {tab === 'suscripciones' && (
            suscripciones.length === 0 ? <Empty texto="No tiene suscripciones." /> :
            suscripciones.map(s => <SuscripcionDetalle key={s.id} s={s} />)
          )}

          {/* Carrito guardado en la cuenta: qué dejó sin comprar y si se topó con un error.
              Antes el carrito vivía solo en el navegador del cliente y quedábamos ciegos. */}
          {tab === 'pedidos' && carrito && (
            <div style={{
              marginBottom: 14, padding: '12px 14px', borderRadius: 10,
              background: carrito.ultimo_error ? '#FEF2F2' : '#FFFBEB',
              border: `1px solid ${carrito.ultimo_error ? '#FECACA' : '#FDE68A'}`,
            }}>
              <div style={{ fontWeight: 800, fontSize: 13, color: carrito.ultimo_error ? '#991B1B' : '#92400E', marginBottom: 6 }}>
                {carrito.ultimo_error ? '⚠ Se trabó con un error' : '🛒 Carrito sin comprar'}
              </div>
              {Array.isArray(carrito.items) && carrito.items.length > 0 ? (
                <>
                  <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                    {carrito.items.map((it, i) => (
                      <div key={i}>{Number(it.cantidad || 1)}× {it.nombre}{it.presentacion ? ` · ${it.presentacion}` : ''}</div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12.5, marginTop: 6, fontWeight: 700, color: '#1a3461' }}>
                    Total estimado {fmtMoney(carrito.items.reduce((s, it) => s + Number(it.precio_unit || 0) * Number(it.cantidad || 1), 0))}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: 12.5, color: '#64748b' }}>Sin productos en el carrito.</div>
              )}
              {carrito.ultimo_error && (
                <div style={{ fontSize: 12, color: '#991B1B', marginTop: 8, fontFamily: 'ui-monospace, monospace', background: '#fff', padding: '6px 8px', borderRadius: 6 }}>
                  {carrito.ultimo_error}
                  {carrito.error_at && <span style={{ color: '#94a3b8' }}> · {fmtFecha(carrito.error_at)}</span>}
                </div>
              )}
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 8 }}>
                Última actividad {fmtFecha(carrito.updated_at)}
                {carrito.ultimo_paso && ` · quedó en: ${carrito.ultimo_paso}`}
              </div>
            </div>
          )}

          {tab === 'pedidos' && (
            pedidos.length === 0 ? <Empty texto="No tiene pedidos." /> :
            <div style={{ maxHeight: 480, overflowY: 'auto' }}>
              {pedidos.map(p => (
                <ItemCard key={p.id}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong>{p.numero_orden}</strong>
                      <span style={pill(p.estado === 'pagado' ? '#D1FAE5' : p.estado === 'pendiente_pago' ? '#FEF3C7' : '#F1F5F9', '#475569')}>{p.estado}</span>
                      <span style={pill('#dbeafe', '#1e40af')}>{p.metodo_pago || '—'}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                      📅 {fmtFecha(p.created_at)}
                      {p.fecha_entrega_programada && ` · entrega ${fmtFecha(p.fecha_entrega_programada)}`}
                    </div>
                  </div>
                  <strong style={{ fontSize: 14, color: '#1a3461' }}>{fmtMoney(p.monto_total)}</strong>
                </ItemCard>
              ))}
            </div>
          )}

          {tab === 'facturas' && (
            facturas.length === 0 ? <Empty texto="No tiene facturas emitidas." /> :
            facturas.map(f => {
              const esNC = f.tipo_cfe === 'e_nota_credito'
              return (
                <ItemCard key={f.id}>
                  <div style={{ flex: 1 }}>
                    <strong>{esNC ? '↩ NC ' : '🧾 '}{f.serie}-{f.numero}</strong>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{fmtFecha(f.fecha_emision)}</div>
                  </div>
                  <strong style={{ fontSize: 14, color: esNC ? '#dc2626' : '#1a3461' }}>{esNC ? '−' : ''}{fmtMoney(f.monto_total)}</strong>
                  {f.shortlink_hash && <a href={`https://limpack.uy/f/${f.shortlink_hash}`} target="_blank" rel="noopener noreferrer" style={btnLink}>📄 PDF</a>}
                </ItemCard>
              )
            })
          )}

          {tab === 'facturacion' && (
            perfilesFact.length === 0 ? <Empty texto="No tiene perfiles de facturación." /> :
            perfilesFact.map(pf => (
              <ItemCard key={pf.id}>
                <div style={{ flex: 1 }}>
                  <strong>{pf.alias || pf.razon_social || pf.tipo}</strong>
                  {pf.es_default && <span style={{ ...pill('#dbeafe', '#1e40af'), marginLeft: 6 }}>default</span>}
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                    {pf.tipo}{pf.rut ? ` · RUT ${pf.rut}` : ''}{pf.email_fiscal ? ` · ${pf.email_fiscal}` : ''}
                  </div>
                </div>
              </ItemCard>
            ))
          )}
        </div>

        {/* Timeline */}
        {timeline.length > 0 && (
          <div style={card}>
            <div style={cardTitle}>📅 Actividad reciente</div>
            <div>
              {timeline.map((it, i) => (
                <div key={i} style={tlRow}>
                  <div style={tlIcon}>{it.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: '#1a3461', fontWeight: 600 }}>{it.titulo}</div>
                    {it.subtitulo && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.subtitulo}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtFechaCorta(it.at)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CSS responsive (mobile-first) */}
        <style>{`
          @media (max-width: 640px) {
            .ud-hero { flex-direction: column; align-items: center; text-align: center; }
            .ud-hero h1 { font-size: 18px !important; }
            .ud-topbar { flex-direction: column; align-items: stretch; gap: 8px; }
            .ud-topbar > div { justify-content: space-between; }
            .ud-twocol { grid-template-columns: 1fr !important; }
            .ud-tabs button { font-size: 12px !important; padding: 8px 10px !important; }
          }
        `}</style>

        {/* Barra flotante de guardado: aparece SOLO si hay ediciones sin guardar. El botón de la
            barra superior queda fuera de pantalla al editar Dirección → se perdían cambios. */}
        {hayCambios && (
          <div style={{
            position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 200,
            background: '#1a3461', color: '#fff', padding: '12px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
            boxShadow: '0 -6px 20px rgba(0,0,0,.18)', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Tenés cambios sin guardar</span>
            <button
              onClick={guardar}
              disabled={saving}
              style={{
                padding: '9px 20px', background: '#00A8B5', color: '#052b30', border: 0,
                borderRadius: 8, fontSize: 13, fontWeight: 800, cursor: saving ? 'default' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {saving ? 'Guardando…' : '💾 Guardar cambios'}
            </button>
            <button
              onClick={() => setForm(f => ({
                ...f,
                nombre: perfil.nombre || '', email: perfil.email || '', phone: perfil.phone || '',
                direccion: perfil.direccion || '', apto: perfil.apto || '', barrio_id: perfil.barrio_id || '',
                documento_tipo: perfil.documento_tipo || '', documento: perfil.documento || '',
                transferencia_habilitada: !!perfil.transferencia_habilitada,
                tags: perfil.tags || [], notas_admin: perfil.notas_admin || '',
              }))}
              style={{
                padding: '9px 14px', background: 'transparent', color: 'rgba(255,255,255,.75)',
                border: '1px solid rgba(255,255,255,.3)', borderRadius: 8, fontSize: 12.5,
                fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Descartar
            </button>
          </div>
        )}

        {renovClave && (
          <ModalRenovarClave
            perfil={perfil}
            telE164={telE164}
            state={renovClave}
            onClose={() => setRenovClave(null)}
          />
        )}
      </div>
    </AdminLayout>
  )
}

// ─────────────── Modal renovar clave ───────────────
function ModalRenovarClave({ perfil, telE164, state, onClose }) {
  const [copiado, setCopiado] = useState(false)
  async function copiar() {
    if (!state.link) return
    await navigator.clipboard.writeText(state.link)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }
  function abrirWhatsApp() {
    if (!state.link) return
    const nombre = (perfil.nombre || 'che').split(' ')[0]
    const msg = `Hola ${nombre}! Te paso el link para renovar tu contraseña en Limpack:\n\n${state.link}\n\nEs de un solo uso, entrás y elegís una nueva.`
    const url = telE164
      ? `https://wa.me/${telE164}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`
    window.open(url, '_blank', 'noopener')
  }
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: 16, backdropFilter: 'blur(2px)',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 16, padding: '24px 22px',
        maxWidth: 460, width: '100%', boxShadow: '0 24px 60px rgba(15,23,42,.35)',
        position: 'relative',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 12, right: 12,
          background: 'transparent', border: 0, fontSize: 18, cursor: 'pointer', color: '#94a3b8',
          width: 32, height: 32, borderRadius: '50%',
        }}>✕</button>
        <h2 style={{ margin: '0 0 6px', color: '#1a3461', fontSize: 18 }}>🔑 Renovar clave</h2>
        <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
          Para <strong>{perfil.nombre || perfil.email}</strong>
        </p>

        {state.loading && <div style={{ padding: '20px 0', textAlign: 'center', color: '#64748b' }}>Generando link…</div>}

        {state.error && (
          <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>
            Error: {state.error}
          </div>
        )}

        {state.link && (
          <>
            <div style={{ padding: 12, background: '#dcfce7', border: '1px solid #86efac', borderRadius: 10, marginBottom: 14 }}>
              <div style={{ fontWeight: 700, color: '#166534', fontSize: 13 }}>✓ Link generado</div>
              <div style={{ fontSize: 12, color: '#15803d', marginTop: 3 }}>
                Es de un solo uso. Al entrar elige una nueva contraseña.
              </div>
            </div>

            <button onClick={abrirWhatsApp} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              background: '#25d366', color: '#fff', border: 0, padding: '12px 16px',
              borderRadius: 12, fontWeight: 700, fontSize: 14, cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(37,211,102,.35)', fontFamily: 'inherit',
            }}>
              <span style={{
                width: 32, height: 32, background: 'rgba(255,255,255,.2)',
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16,
              }}>💬</span>
              <span>Enviar por WhatsApp</span>
              <span style={{ marginLeft: 'auto', opacity: .85 }}>→</span>
            </button>
            {telE164 && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, textAlign: 'center' }}>
                Abre chat con <strong>{perfil.phone}</strong>
              </div>
            )}

            <button onClick={copiar} style={{
              width: '100%', padding: '10px 14px', marginTop: 10,
              background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
              borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            }}>
              {copiado ? '✓ Copiado' : '📋 Copiar link'}
            </button>

            <details style={{ marginTop: 12 }}>
              <summary style={{ fontSize: 11, color: '#94a3b8', cursor: 'pointer' }}>Ver link completo</summary>
              <div style={{
                marginTop: 6, padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 8, fontSize: 10.5, fontFamily: 'ui-monospace, monospace',
                wordBreak: 'break-all', color: '#475569', lineHeight: 1.5,
              }}>
                {state.link}
              </div>
            </details>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18, paddingTop: 14, borderTop: '1px solid #f1f5f9' }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', background: '#fff', border: '1px solid #cbd5e1',
            color: '#475569', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
          }}>Cerrar</button>
        </div>
      </div>
    </div>
  )
}

// ─────────── subcomponentes ───────────
function Kpi({ label, value, hint, color = '#1a3461' }) {
  return (
    <div style={kpiCard}>
      <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 900, color, marginTop: 2, lineHeight: 1.2 }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{hint}</div>}
    </div>
  )
}

function MiniBarChart({ data, max }) {
  // Render con DIVs en lugar de SVG: tipografía nativa, no se pixela al escalar.
  // Cada columna = bar + label mes.
  const totalConData = data.filter(m => m.total > 0).length
  if (totalConData === 0) {
    return (
      <div style={{ padding: '24px 16px', textAlign: 'center', color: '#cbd5e1', fontSize: 12, fontStyle: 'italic' }}>
        Sin compras registradas todavía
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, padding: '8px 0' }}>
      {data.map((m, i) => {
        const pct = max > 0 ? (m.total / max) * 100 : 0
        const hasData = m.total > 0
        return (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0, height: '100%' }}>
            <div style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', position: 'relative' }}>
              {hasData && (
                <div style={{ fontSize: 9, fontWeight: 800, color: '#1a3461', marginBottom: 2, whiteSpace: 'nowrap' }}>
                  {m.total >= 1000 ? `${Math.round(m.total / 1000)}k` : `$${Math.round(m.total)}`}
                </div>
              )}
              <div
                title={`${m.label}: $${Math.round(m.total).toLocaleString('es-UY')}`}
                style={{
                  width: '78%',
                  height: hasData ? `${Math.max(4, pct)}%` : 3,
                  background: hasData ? 'linear-gradient(180deg, #00A8B5 0%, #007a82 100%)' : '#e2e8f0',
                  borderRadius: '4px 4px 2px 2px',
                  transition: 'height .3s',
                  minHeight: 3,
                }}
              />
            </div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 6, fontWeight: 600, textTransform: 'lowercase' }}>{m.label}</div>
          </div>
        )
      })}
    </div>
  )
}

// ─────────── SuscripcionDetalle: drill-down completo de un cliente ───────────
function SuscripcionDetalle({ s }) {
  const tieneGps = s.lat != null && s.lng != null
  const direccionCompleta = [s.direccion, s.apto && `Apto ${s.apto}`, s.barrio_obj?.nombre].filter(Boolean).join(' · ')

  // Map embed via OpenStreetMap (gratis, sin API key)
  const mapaUrl = tieneGps
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${s.lng - 0.003},${s.lat - 0.002},${s.lng + 0.003},${s.lat + 0.002}&layer=mapnik&marker=${s.lat},${s.lng}`
    : null
  const mapaLinkExterno = tieneGps
    ? `https://www.google.com/maps?q=${s.lat},${s.lng}`
    : null

  const estadoColor = {
    activa:    { bg: '#D1FAE5', color: '#065F46' },
    pausada:   { bg: '#FEF3C7', color: '#92400E' },
    cancelada: { bg: '#fee2e2', color: '#991b1b' },
  }[s.estado] || { bg: '#F1F5F9', color: '#475569' }

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 14 }}>
      {/* Header card */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 16, color: '#1a3461' }}>{s.alias || s.kit?.nombre || s.kit_actual || 'Sin kit'}</strong>
            <span style={pill(estadoColor.bg, estadoColor.color)}>{s.estado}</span>
            {s.fuera_zona && <span style={pill('#FED7AA', '#9A3412')}>🚫 fuera zona</span>}
            {s.empresa && <span style={pill('#fef3c7', '#854d0e')}>🏢 B2B</span>}
            {s.modelo_cobro && <span style={pill('#e0e7ff', '#3730a3')}>{s.modelo_cobro}</span>}
          </div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
            {s.kit?.codigo && `Kit ${s.kit.codigo} · `}Inicio {fmtFecha(s.fecha_inicio)}
            {s.empresa && (
              <> · 🏢 <strong style={{ color: '#854d0e' }}>{s.empresa.razon_social}</strong>
                {s.empresa.rut && <span style={{ fontFamily: 'monospace', fontSize: 11 }}> · RUT {s.empresa.rut}</span>}
              </>
            )}
          </div>
        </div>
        <Link to={`/admin/clientes?cliente=${s.id}`} style={btnLink}>Editar →</Link>
      </div>

      {/* Grid 2 columnas: izquierda info, derecha mapa */}
      <div style={{ display: 'grid', gridTemplateColumns: tieneGps ? '1fr 280px' : '1fr', gap: 12 }}>
        <div>
          {/* Dirección */}
          <Bloque icon="📍" titulo="Dirección de entrega">
            <div style={{ fontSize: 13, color: '#1a3461', fontWeight: 600 }}>{direccionCompleta || '— sin dirección —'}</div>
            {s.tipo_vivienda && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{tipoViviendaIcon(s.tipo_vivienda)} {s.tipo_vivienda}</div>}
            {!tieneGps && s.direccion && (
              <div style={{ marginTop: 6, fontSize: 11, color: '#f59e0b' }}>⚠ Sin coordenadas GPS · geocodificar en /admin/clientes</div>
            )}
            {tieneGps && s.geocoded_at && (
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>GPS: {Number(s.lat).toFixed(5)}, {Number(s.lng).toFixed(5)} · {fmtFecha(s.geocoded_at)}</div>
            )}
          </Bloque>

          {/* Horarios */}
          <Bloque icon="📅" titulo="Entrega">
            <KvRow k="Día" v={s.dia_entrega ? capitalize(s.dia_entrega) : '—'} />
            <KvRow k="Ventana" v={s.ventana_horaria ? ventanaLabel(s.ventana_horaria) : '—'} />
            <KvRow k="Semana del mes" v={s.semana_mes ? semanaLabel(s.semana_mes) : '—'} />
            {s.fuera_zona && s.entrega_responsable && (
              <KvRow k="Responsable" v={s.entrega_responsable} highlight />
            )}
            {s.pausa_hasta && (
              <KvRow k="Pausada hasta" v={fmtFecha(s.pausa_hasta)} highlight />
            )}
          </Bloque>

          {/* Pago */}
          <Bloque icon="💳" titulo="Pago">
            {s.mp_card_last4 ? (
              <>
                <KvRow k="Tarjeta" v={`${s.mp_card_brand?.toUpperCase() || 'TARJETA'} ···${s.mp_card_last4}`} />
                <KvRow k="Estado MP" v={s.mp_preapproval_status || '—'} />
              </>
            ) : (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>Sin tarjeta registrada</div>
            )}
            {s.dia_cobro_mes && <KvRow k="Día cobro" v={`${s.dia_cobro_mes} de cada mes`} />}
            {s.fecha_proximo_cobro && <KvRow k="Próximo cobro" v={fmtFecha(s.fecha_proximo_cobro)} highlight />}
          </Bloque>

          {/* Referidos/contexto */}
          {(s.portero || s.cancelacion_motivo) && (
            <Bloque icon="🔗" titulo="Contexto">
              {s.portero && (
                <KvRow k="Portero referido" v={`${s.portero.edificio_nombre || s.portero.lugar || '—'}${s.portero.direccion ? ' · ' + s.portero.direccion : ''}`} />
              )}
              {s.cancelacion_motivo && (
                <KvRow k="Motivo cancelación" v={s.cancelacion_motivo} highlight />
              )}
            </Bloque>
          )}

          {/* Notas */}
          {s.notas && (
            <Bloque icon="📝" titulo="Notas para repartidor">
              <div style={{ fontSize: 13, color: '#1a3461', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{s.notas}</div>
            </Bloque>
          )}
        </div>

        {/* Mapa */}
        {tieneGps && (
          <div className="ud-mapa-col">
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#f8fafc' }}>
              <iframe
                src={mapaUrl}
                title={`Mapa de ${s.alias || s.id}`}
                width="100%" height="200"
                style={{ border: 0, display: 'block' }}
                loading="lazy"
              />
              <a
                href={mapaLinkExterno} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', padding: '8px 10px', fontSize: 11, color: '#475569', background: '#fff', borderTop: '1px solid #e2e8f0', textDecoration: 'none', fontWeight: 700, textAlign: 'center' }}
              >🗺 Abrir en Google Maps →</a>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────── helpers de SuscripcionDetalle ───────────
function Bloque({ icon, titulo, children }) {
  return (
    <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>
        {icon} {titulo}
      </div>
      {children}
    </div>
  )
}
function KvRow({ k, v, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12, padding: '3px 0' }}>
      <span style={{ color: '#94a3b8' }}>{k}</span>
      <span style={{ color: highlight ? '#dc2626' : '#1a3461', fontWeight: highlight ? 700 : 600, textAlign: 'right' }}>{v}</span>
    </div>
  )
}
function tipoViviendaIcon(t) {
  return { casa: '🏠', edificio: '🏢', oficina: '🏢', ph: '🏠' }[t?.toLowerCase()] || '🏠'
}
function capitalize(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '' }
function ventanaLabel(v) {
  return {
    manana: '🌅 Mañana (9-13)',
    tarde: '🌇 Tarde (13-18)',
    noche: '🌙 Noche (18-21)',
    todo_dia: '☀ Todo el día',
    flexible: '↔ Flexible',
  }[v] || v
}
function semanaLabel(s) {
  return { primera: '1ra del mes', segunda: '2da del mes', tercera: '3ra del mes', cuarta: '4ta del mes', cualquiera: 'Cualquiera' }[s] || s
}

function FieldL({ label, children, hint }) {
  return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <span style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#475569', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 5 }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11, color: '#64748B', marginTop: 3 }}>{hint}</span>}
    </label>
  )
}
function Grid2({ children }) { return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>{children}</div> }
function ItemCard({ children }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8 }}>{children}</div>
}
function Empty({ texto }) {
  return <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 13, background: '#F8FAFC', border: '1px dashed #cbd5e1', borderRadius: 10 }}>{texto}</div>
}

// ─────────── estilos ───────────
const wrap = { maxWidth: 1100, margin: '0 auto', padding: '12px 14px 60px' }
const topBar = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }
const hero = { display: 'flex', alignItems: 'center', gap: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }
const avatar = (bg) => ({ width: 56, height: 56, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 })
const heroNombre = { margin: 0, fontSize: 22, color: '#1a3461', fontWeight: 900 }
const heroSub = { fontSize: 12, color: '#64748b', display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 2 }
const badgesRow = { display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 8 }
const pill = (bg, color) => ({ display: 'inline-block', padding: '3px 9px', background: bg, color, borderRadius: 999, fontSize: 11, fontWeight: 700, lineHeight: 1.4 })
const kpiGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 12 }
const kpiCard = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', textAlign: 'left' }
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14, marginBottom: 12 }
const cardTitle = { fontSize: 12, fontWeight: 800, color: '#1a3461', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 10 }
const twoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, color: '#1a3461', fontFamily: 'inherit', boxSizing: 'border-box' }
const ro  = { padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, color: '#1a3461', minHeight: 18 }
const textarea = { ...inp, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }
const tabsBar = { display: 'flex', gap: 4, marginBottom: 0, borderBottom: '1px solid #e2e8f0', overflowX: 'auto', flexWrap: 'wrap' }
const tabBtn = { padding: '10px 14px', background: 'transparent', border: 0, fontSize: 13, fontWeight: 700, color: '#94a3b8', cursor: 'pointer', borderBottom: '3px solid transparent', marginBottom: -1, whiteSpace: 'nowrap', fontFamily: 'inherit' }
const tabActive = { ...tabBtn, color: '#1a3461', borderBottomColor: '#00A8B5' }
const btnBack = { padding: '7px 14px', background: '#fff', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#475569', cursor: 'pointer', fontFamily: 'inherit' }
const btnQuickCRM = { padding: '7px 12px', background: '#1a3461', color: '#fff', border: 0, borderRadius: 8, fontSize: 12, fontWeight: 800, textDecoration: 'none', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center' }
const btnQuickWa = { padding: '7px 12px', background: '#25D366', color: '#fff', border: 0, borderRadius: 8, fontSize: 12, fontWeight: 800, textDecoration: 'none', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center' }
const btnQuickMail = { padding: '7px 12px', background: '#fff', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: 'none', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center' }
const btnQuickClave = { padding: '7px 12px', background: '#FEF3C7', border: '1px solid #FCD34D', color: '#92400e', borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center' }
const btnGuardar = { padding: '7px 14px', background: '#1a3461', color: '#fff', border: 0, borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }
const btnSecond = { padding: '7px 12px', background: '#fff', border: '1px solid #cbd5e1', color: '#475569', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }
const btnLink = { padding: '5px 10px', background: '#F0FDFA', border: '1px solid #A7F3D0', color: '#0e7490', borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: 'none', fontFamily: 'inherit' }
const tagX = { background: 'transparent', border: 0, color: 'inherit', cursor: 'pointer', fontSize: 14, padding: 0, marginLeft: 2, opacity: 0.7, fontWeight: 700 }
const tagSug = { padding: '3px 9px', background: '#fff', border: '1px dashed #cbd5e1', color: '#64748b', borderRadius: 999, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }
const permisosBox = { background: '#F0FDFA', border: '1px solid #A7F3D0', borderRadius: 10, padding: 14 }
const errBox = { padding: 10, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 12 }
const tlRow = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', borderBottom: '1px solid #f1f5f9' }
const tlIcon = { width: 30, height: 30, background: '#f1f5f9', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }

// Para que las clases CSS responsive matcheen
const wrapWithClass = wrap
